import { bindDialog, BindDialogReturn, DialogProps, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { MenuItem } from "@mui/material";
import { addDays } from "date-fns";
import { useEffect, useMemo, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CustomerTypeEnum } from "../../common/enums/CustomerTypeEnum";
import { RulesEngineRuleCustomerTypeEnum } from "../../common/enums/RulesEngineRuleCustomerTypeEnum";
import { RulesEngineRuleTypeEnum } from "../../common/enums/RulesEngineTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { getObjectHistoryItemTypeName, IObjectHistoryDiagnosticEventItem, ObjectHistoryItemTypeEnum } from "../../queries/object-history/Common";
import { useRulesEngineRules } from "../../queries/object-history/UseRulesEngineRules";
import { useSaveHistoryItem } from "../../queries/object-history/UseSaveHistoryItem";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IRulesEngineRuleModel } from "../../services/rules-engine-data-service/Models";
import FormDateTimePicker from "../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../forms/form-input/FormInput";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type DiagnosticEventEditDialogInitValues = IObjectHistoryDiagnosticEventItem;

export type DiagnosticEventEditDialogProps = {
    groupID: number;
    contextObject: IeLynxObject;
} & DialogProps<DiagnosticEventEditDialogInitValues>;

type DiagnosticEventDialogFormValuesType = {
    startTime: Date;
    endTime: Date;
    //diagnosis: string; // TODO: Enum
    ruleIndex: number;
    note: string;
}

export default function DiagnosticEventEditDialog({
    open,
    onClose,
    values,
    groupID,
    contextObject
}: DiagnosticEventEditDialogProps): JSX.Element {
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const { t } = useTranslation();
    const isAddMode = !!values?.ruleName;

    const { data: rules/*, status*/ } = useQueryNotifications(useRulesEngineRules)({
        ruleID: undefined,
        ruleType: undefined,
        runType: undefined,
        customerType: userState.customer?.customerType === CustomerTypeEnum.Energy
            ? RulesEngineRuleCustomerTypeEnum.Energy
            : userState.customer?.customerType === CustomerTypeEnum.Water
                ? RulesEngineRuleCustomerTypeEnum.Water
                : RulesEngineRuleCustomerTypeEnum.All
    });

    const diagnosticRules: IRulesEngineRuleModel[] = useMemo(() => {
        // ensure eLynx rules are at the top
        const eLynxRules = rules?.filter(o => o.ruleType === RulesEngineRuleTypeEnum.ElynxDiagnostic) ?? [];
        const customerRules = rules?.filter(o => o.ruleType === RulesEngineRuleTypeEnum.CustomDiagnostic) ?? [];
        return eLynxRules.concat(customerRules);
    }, [
        rules
    ]);

    const { mutate: saveDiagnosticEvent/*, status: saveDiagnosticEventStatus*/ } = useQueryNotifications(useSaveHistoryItem)({
        customerID: userState.customer?.id as number,
        groupID: groupID,
        contextObject: contextObject,
        offline: offlineState.offline,
        onSuccess: (_data, variables) => {
            const itemName = getObjectHistoryItemTypeName(variables);
            acknowledge(t("saveSuccessMessage", { itemName: itemName }));
        }
    });

    const form = useForm<DiagnosticEventDialogFormValuesType>({
        mode: "onChange",
        defaultValues: {
            startTime: new Date(),
            endTime: new Date(),
            ruleIndex: -1,
            note: ""
        } as DiagnosticEventDialogFormValuesType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset
    } = form;

    useEffect(() => {
        const now = new Date();
        const rule = diagnosticRules?.find(o => o.ruleName === values?.ruleName);

        reset({
            startTime: values?.startTime ?? addDays(now, -1),
            endTime: values?.endTime ?? now,
            ruleIndex: rule?.id ?? -1,
            note: values?.note ?? ""
        });
    }, [reset, values, diagnosticRules]);

    async function onOkay(data: DiagnosticEventDialogFormValuesType) {
        const rule = rules?.find(o => o.id === data.ruleIndex);

        const eventItem = {
            ...(values ?? []),
            startTime: data.startTime,
            endTime: data.endTime,
            ruleID: -1, // rule?.id as number,
            ruleName: rule?.ruleName,
            note: data.note,
            type: ObjectHistoryItemTypeEnum.DiagnosticEvent,
            objectID: contextObject.id,
            objectType: contextObject.objectType,
            objectName: contextObject.displayText
        } as IObjectHistoryDiagnosticEventItem;

        saveDiagnosticEvent(eventItem);

        reset();

        onClose && onClose({
            reason: "ok"
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                "reason": "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function validateRule(value: number) {
        return value> -1;
    }

    return (
        <>
            <ResponsiveDialog
                title={isAddMode ? t("diagnosticEventDialog.addDiagnosticEvent") : t("diagnosticEventDialog.editDiagnosticEvent")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                <Form form={form}>
                    <FormDateTimePicker
                        name="startTime"
                        label={t("datetime.startTime")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />

                    <FormDateTimePicker
                        name="endTime"
                        label={t("datetime.endTime")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: true
                        }}
                    />

                    <FormSelect
                        name="ruleIndex"
                        label={t("diagnosticEventDialog.diagnosis")}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            validate: validateRule
                        }}
                    >
                        <MenuItem key={-1} value={-1} disabled={true}>({t("diagnosticEventDialog.diagnosisHint")})</MenuItem>
                        {diagnosticRules?.map(o =>
                            <MenuItem key={o.id} value={o.id}>{o.ruleName}</MenuItem>
                        )}
                    </FormSelect>

                    <FormInput
                        name="note"
                        label={t("diagnosticEventDialog.note")}
                        multiline={true}
                        rows={6}
                        sx={{
                            marginTop: 2
                        }}
                        rules={{
                            required: false
                        }}
                    />
                </Form>
            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    )
}

export type DiagnosticEventEditDialogState = DialogState<DiagnosticEventEditDialogInitValues>;
export type BindDiagnosticEventEditDialogReturn = BindDialogReturn<DiagnosticEventEditDialogInitValues>;

export function useDiagnosticEventEditDialogState(): DiagnosticEventEditDialogState {
    return useDialogState();
}

export function bindDiagnosticEventEditDialog(props: DiagnosticEventEditDialogState): BindDiagnosticEventEditDialogReturn {
    return bindDialog(props);
}